import { PayloadAction } from "@reduxjs/toolkit";
import { AxiosResponse } from "axios";
import { call, put, takeEvery } from "redux-saga/effects";
import { Holder } from "../../interfaces/Holder";
import { Paginated } from "../../interfaces/Paginated";
import { googleFunctionService } from "../../services/axios";
import { buildPaginationQueryString } from "../../utils/queryStringPaginationOptions";
import * as sagaActions from "./sagaActions";
import { actions } from "./slice";
import * as HolderActionTypes from "./types";

export function* getAllHoldersSaga(
  action: ReturnType<typeof sagaActions.loadAllHolders>
) {
  yield put(
    actions.setLoader({ fetchableProp: "allHolders", isLoading: true })
  );

  try {
    const queryParams = buildPaginationQueryString(action.payload);
    const { data }: AxiosResponse<Paginated<Holder>> = yield call(
      googleFunctionService,
      `/holder/?${queryParams}`,
      {
        method: "GET",
      }
    );
    yield put(actions.setPaginatedHolders(data));
  } catch (err) {
    yield put(actions.setError({ fetchableProp: "allHolders", error: err }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "allHolders", isLoading: false })
    );
  }
}

export function* createHolderSaga({
  payload,
}: PayloadAction<Omit<Holder, "uid">>) {
  yield put(
    actions.setLoader({ fetchableProp: "createdHolder", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Holder> = yield call(
      googleFunctionService,
      "/holder",
      {
        method: "POST",
        data: payload,
      }
    );
    yield put(actions.createHolder(data));
  } catch (err) {
    yield put(actions.setError({ fetchableProp: "createdHolder", error: err }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "createdHolder", isLoading: false })
    );
  }
}

export function* updateHolderSaga({
  payload,
}: ReturnType<typeof sagaActions.updateHolder>) {
  yield put(
    actions.setLoader({ fetchableProp: "updatedHolder", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Holder> = yield call(
      googleFunctionService,
      `/holder/${payload.uid}`,
      {
        method: "PUT",
        data: payload,
      }
    );
    yield put(actions.updateHolder(data));
  } catch (err) {
    yield put(actions.setError({ fetchableProp: "updatedHolder", error: err }));
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "updatedHolder", isLoading: false })
    );
  }
}

export function* deleteHolderSaga({
  payload,
}: ReturnType<typeof sagaActions.deleteHolderById>) {
  yield put(
    actions.setLoader({ fetchableProp: "deletedHolder", isLoading: true })
  );

  try {
    const { data }: AxiosResponse<Holder> = yield call(
      googleFunctionService,
      `/user/${payload}`,
      {
        method: "DELETE",
      }
    );
    yield put(actions.deleteHolder(data));
  } catch (err) {
  } finally {
    yield put(
      actions.setLoader({ fetchableProp: "deletedHolder", isLoading: false })
    );
  }
}

export function* resetPasswordSaga({
  payload,
}: ReturnType<typeof sagaActions.resetPasswordById>) {
  actions.setLoader({ fetchableProp: "passwordReset", isLoading: true });

  try {
    yield call(googleFunctionService.patch, `/user/${payload}/reset-password`);
    yield put(actions.resetPassword(true));
  } catch (err) {
    yield put(actions.resetPassword(false));
  } finally {
    actions.setLoader({ fetchableProp: "passwordReset", isLoading: false });
  }
}

export function* getHolderByIdSaga({
  payload,
}: ReturnType<typeof sagaActions.loadHolderById>) {
  actions.setLoader({ fetchableProp: "holder", isLoading: true });

  try {
    const { data }: AxiosResponse<Holder> = yield call(
      googleFunctionService,
      `/user/${payload}`,
      {
        method: "GET",
      }
    );
    yield put(actions.setHolder(data));
  } catch (err) {
  } finally {
    actions.setLoader({ fetchableProp: "holder", isLoading: false });
  }
}

export default function* rootSaga() {
  yield takeEvery(
    HolderActionTypes.fetch_paginated_holders_saga,
    getAllHoldersSaga
  );
  yield takeEvery(HolderActionTypes.fetch_holder_by_id_saga, getHolderByIdSaga);
  yield takeEvery(HolderActionTypes.create_new_holder_saga, createHolderSaga);
  yield takeEvery(HolderActionTypes.delete_holder_saga, deleteHolderSaga);
  yield takeEvery(HolderActionTypes.update_holder_saga, updateHolderSaga);
  yield takeEvery(
    HolderActionTypes.reset_password_holder_saga,
    resetPasswordSaga
  );
}
